import { CommandDescriptor, ParsedCommand } from '../../types/commandTypes'

/**
 * Result of splitting a raw input string into structured slash-commands and the
 * remaining bot message (with the slash tokens stripped).
 */
export interface ParseResult {
  commands: ParsedCommand[]
  /** The message with all leading slash-command tokens removed. */
  strippedMessage: string
}

/**
 * Resolve a typed token to a structured command, case-insensitively:
 * - A command's `name` or one of its `aliases` (e.g. "org", "select") ->
 *   that command with no args.
 * - An entity shorthand: a token matching one of a command's options by
 *   `value` (the access_scope tag, e.g. "dkb") or `name` -> that command
 *   with the option's canonical `value` as its sole arg, so `/dkb` becomes
 *   `{name: "org", args: ["DKB"]}`.
 *
 * Returns `null` when the token matches nothing.
 */
function resolveToken(token: string, descriptors: CommandDescriptor[]): ParsedCommand | null {
  const lower = token.toLowerCase()
  for (const descriptor of descriptors) {
    if (descriptor.name.toLowerCase() === lower) {
      return { name: descriptor.name, args: [] }
    }
    if (descriptor.aliases.some(alias => alias.toLowerCase() === lower)) {
      return { name: descriptor.name, args: [] }
    }
  }
  for (const descriptor of descriptors) {
    for (const option of descriptor.options) {
      if (option.value.toLowerCase() === lower || option.name.toLowerCase() === lower) {
        return { name: descriptor.name, args: [option.value] }
      }
    }
  }
  return null
}

/** Append `args` onto `command.args`, skipping ones already present. */
function appendArgs(command: ParsedCommand, args: string[]): void {
  for (const arg of args) {
    if (!command.args.includes(arg)) {
      command.args.push(arg)
    }
  }
}

/**
 * Whether the user is still typing the leading `/command` token — i.e. the
 * input starts with a slash and no whitespace follows the token yet. Once a
 * space appears (`/org hello`) the user is writing the message, not picking
 * from the popup, so the popup should close and Enter should send as usual.
 */
export function isTypingCommandToken(input: string): boolean {
  return input.startsWith('/') && !/^\/\S*\s/.test(input)
}

export const AGENT_COMMAND = 'agent'
export const MAIN_CHAT_PICK = 'main_chat'
// Sent by the X on the active agent: the next question is routed afresh.
export const LEAVE_AGENT_PICK = 'leave'

/** Still typing a leading `@name`: no space yet, or the text still starts one of `agentNames`. */
export function isTypingAgentToken(input: string, agentNames: string[]): boolean {
  if (!input.startsWith('@')) return false
  if (!/^@\S*\s/.test(input)) return true
  const typed = input.slice(1).toLowerCase()
  return agentNames.some(name => name.toLowerCase().startsWith(typed))
}

/**
 * Pure parser: extract leading whitespace-delimited `/token` slash-commands from
 * a raw input string and return them as structured commands plus the remaining
 * message with those tokens stripped.
 *
 * Behaviour:
 * - No leading slash -> empty commands, message unchanged (only trimmed of the
 *   leading whitespace it consumed).
 * - `/DKB hello` -> command {name:"org", args:["DKB"]} (entity shorthand via the
 *   descriptor's options) + message "hello".
 * - Multiple consecutive leading tokens are all parsed; tokens resolving to the
 *   same command union their args (`/dkb /slob` -> one org command with both tags).
 * - A `/` that is not at a token boundary (mid-word, e.g. "a/b") is NOT a command.
 * - When `descriptors` are supplied, a token is only treated as a command when it
 *   resolves to a known command name/alias or option; the canonical name/value is
 *   used. Unknown `/tokens` are left in the message untouched. When `descriptors`
 *   is empty the raw token (without the leading slash) is used as the command name.
 *
 * Note: this handles the typed-shorthand path. Popup-driven selections are merged
 * in at the component level via {@link unionCommands}.
 */
export function parseSlashCommands(
  input: string,
  descriptors: CommandDescriptor[] = [],
): ParseResult {
  const commands: ParsedCommand[] = []
  let rest = input.replace(/^\s+/, '')

  // Only leading tokens count. Stop at the first token that is not a slash-command.
  while (rest.startsWith('/')) {
    const match = rest.match(/^\/(\S+)(\s*)/)
    if (!match) {
      break
    }
    const token = match[1]
    const resolved =
      descriptors.length > 0 ? resolveToken(token, descriptors) : { name: token, args: [] }

    if (resolved === null) {
      // Unknown command token: leave it (and the remainder) in the message.
      break
    }

    const existing = commands.find(command => command.name === resolved.name)
    if (existing) {
      appendArgs(existing, resolved.args)
    } else {
      commands.push(resolved)
    }
    rest = rest.slice(match[0].length).replace(/^\s+/, '')
  }

  return { commands, strippedMessage: rest.trim() }
}

/**
 * Identity a command is de-duplicated by. Most commands are singletons keyed by
 * `name` (a second `/org` refines the first). `knowledge` is the exception: it's
 * a per-source toggle carrying `[sourceKey, on|off]`, so several can coexist —
 * one per source — and must be keyed by name + source key or they clobber each
 * other (and the backend requires each to stay a discrete `[sourceKey, on|off]`
 * pair, so their args must never be unioned into one command).
 */
export function commandIdentity(command: ParsedCommand): string {
  return command.name === 'knowledge' ? `knowledge:${command.args[0] ?? ''}` : command.name
}

/**
 * Merge two lists of commands, de-duplicating by {@link commandIdentity} (later
 * entries win so a popup selection can refine/replace a typed shorthand). Order
 * follows first appearance. Use {@link unionCommands} when same-identity args
 * should accumulate instead of replace.
 */
export function mergeCommands(base: ParsedCommand[], extra: ParsedCommand[]): ParsedCommand[] {
  const byIdentity = new Map<string, ParsedCommand>()
  for (const command of [...base, ...extra]) {
    byIdentity.set(commandIdentity(command), command)
  }
  return Array.from(byIdentity.values())
}

/**
 * Union two lists of commands: same-name commands collapse to one entry whose
 * args accumulate in first-appearance order (matching the backend, where
 * repeated invocations of e.g. `/org` union their entity scopes). Used on send
 * to combine popup chips with typed shorthands without one clobbering the other.
 */
export function unionCommands(base: ParsedCommand[], extra: ParsedCommand[]): ParsedCommand[] {
  const byIdentity = new Map<string, ParsedCommand>()
  for (const command of [...base, ...extra]) {
    const identity = commandIdentity(command)
    const existing = byIdentity.get(identity)
    if (existing) {
      appendArgs(existing, command.args)
    } else {
      byIdentity.set(identity, { name: command.name, args: [...command.args] })
    }
  }
  return Array.from(byIdentity.values())
}
